import { createModel } from '@rematch/core';
import { PurchaseOrder, PurchaseOrderCreate, PurchaseOrderUpdate } from '../dto';
import type { RootModel } from './index';
import {
  createPurchaseOrder,
  getPurchaseOrders,
  bulkUpdatePurchaseOrders,
  getShipmentEvent,
  GetPaginatedPurchaseOrdersInput,
  getPurchaseOrdersByIds,
  updatePurchaseOrder,
  createPurchaseOrdersPayment,
  BulkEditPayload,
  getPurchaseOrder,
} from '../services/purchaseOrders';
import { ShipmentEventStatus } from '../common/enums';
import { Pagination } from '../types';

export type PurchaseOrdersState = {
  byId: Record<string, PurchaseOrder>;
  page: string[];
};

interface BulkCreateStatusResponse {
  status: ShipmentEventStatus;
  purchaseOrders: PurchaseOrder[];
}

function normalize(payload: PurchaseOrder[]) {
  return payload.reduce<PurchaseOrdersState['byId']>((obj, item) => {
    obj[item.id] = item;
    return obj;
  }, {});
}

export const purchaseOrders = createModel<RootModel>()({
  state: {
    byId: {},
    page: [],
  } as PurchaseOrdersState,
  reducers: {
    bulkUpdate: (state: PurchaseOrdersState, payload: PurchaseOrder[]) => {
      return {
        page: [...state.page],
        byId: {
          ...state.byId,
          ...normalize(payload),
        },
      };
    },
    read: (state: PurchaseOrdersState, payload: PurchaseOrder[]) => {
      return {
        byId: {
          ...state.byId,
          ...normalize(payload),
        },
        page: payload.map(({ id }) => id),
      };
    },
    update: (state: PurchaseOrdersState, payload: PurchaseOrder) => {
      return {
        ...state,
        byId: {
          ...state.byId,
          [payload.id]: payload,
        },
      };
    },
  },
  effects: (dispatch) => ({
    async createPurchaseOrder(payload: PurchaseOrderCreate): Promise<PurchaseOrder> {
      const order = await createPurchaseOrder(payload);
      return order;
    },
    async getPurchaseOrders(payload: GetPaginatedPurchaseOrdersInput): Promise<Pagination> {
      const { meta, links, purchaseOrders } = await getPurchaseOrders(payload);
      dispatch.purchaseOrders.read(purchaseOrders);
      return { meta, links };
    },
    async getPurchaseOrdersByIds(ids: string[]): Promise<PurchaseOrder[]> {
      try {
        const purchaseOrders = await getPurchaseOrdersByIds(ids);
        dispatch.purchaseOrders.bulkUpdate(purchaseOrders);
        return purchaseOrders;
      } catch (err) {
        throw err;
      }
    },
    async getPurchaseOrderById(id: string) {
      const fetchedOrder = await getPurchaseOrder(id);
      dispatch.purchaseOrders.update(fetchedOrder);
    },
    async bulkEditPurchaseOrders(payload: BulkEditPayload): Promise<void> {
      dispatch.purchaseOrders.bulkUpdate(await bulkUpdatePurchaseOrders(payload));
    },
    async getBulkCreateStatus(eventId: number): Promise<BulkCreateStatusResponse> {
      const { status, purchaseOrders = [] } = await getShipmentEvent(eventId);
      dispatch.purchaseOrders.bulkUpdate(purchaseOrders);
      return { purchaseOrders, status };
    },
    async bulkPayPurchaseOrders(payload: PurchaseOrder[]): Promise<void> {
      try {
        const purchaseOrders = await createPurchaseOrdersPayment(payload);
        dispatch.purchaseOrders.bulkUpdate(purchaseOrders);
      } catch (err) {
        throw err;
      }
    },
    async updatePurchaseOrder(payload: PurchaseOrderUpdate): Promise<PurchaseOrder> {
      const order = await updatePurchaseOrder(payload);
      dispatch.purchaseOrders.update(order);
      return order;
    },
  }),
});
